// Basic
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
// Libs
import { cn } from '@/common-utils/libraries/tailwind/utils';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
// Store & actions
import useRootOrganizationStore from '@/app/store/rootOrganization';
import useUserStore from '@/app/store/modules/user';
import actionsUsers from '@/app/modules/users/monetic/api/actionsUsers';
// Components
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import FooterStep from '@/common-utils/components/renders/modal/components/footers/FooterStep/FooterStep';
import Icon from '@/common-utils/components/renders/icon/Icon';
import Entity from '@/common-utils/components/renders/entity/Entity';
import Select from '@/common-utils/components/ui/select';
import Spinner from '@/common-utils/components/renders/spinner/Spinner';
import TooltipHelper from '@/common-utils/components/renders/tooltip/TooltipHelper';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
import { Button } from '@/common-utils/components/ui/button';
import { Form, FormControl, FormField, FormItem } from '@/common-utils/components/ui/form';
import { Input } from '@/common-utils/components/ui/input';
// Common
import ICONS from '@/common-utils/enums/icons';
import buildDictionary from '@/common-utils/functions/buildDictionary';
import userFields from '../../../fields/userFields';
import { useToast } from '@/common-utils/hooks/use-toast';
import useStepConfig from '@/common-utils/hooks/useStepConfig';
// Style
import './ModalSendMailAddUser.scss';
// Lang
import { nsErrors, nsUser } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.organizationId
 * @returns {JSX.Element} ModalSendMail Component
*/
function ModalSendMailAddUser({ isOpen, setIsOpen, organizationId = undefined }) {

    // Constants
    const MAX_EMAIL_TO_SEND = 5;
    const MAX_STEPS_NUMBER = 2;
    const STEP_TITLE_PREFIX = 'modal.send_mail.steps';

    // Store
    const storeRefetchUsers = useUserStore(s => s.storeRefetchUsers);
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);

    // State
    const [step, setStep] = useState(0);
    const [renderList, setRenderList] = useState([
        { email: '', isValid: true, profile: '', profileId: '' }
    ]);
    const [disableAction, setDisableAction] = useState(true);
    const [mailEntryError, setMailEntryError] = useState('');

    // Toast
    const { toast } = useToast();

    // Api Calls
    const { data: userProfiles, isFetched, error: errorFetchProfiles, refetch } = actionsUsers.getProfiles(rootOrganization.uuid);

    useEffect(() => {
        if (!isFetched) {
            refetch();
        }
        if (errorFetchProfiles) {
            toast({
                description: <Entity namespace={nsUser} entity='errors.fetchProfiles' />,
                variant: 'danger'
            });
        }
    }, [isFetched, errorFetchProfiles]);

    const userProfilesList = userProfiles ? Object.values(userProfiles)
        .map((val) => val.name) : null;

    // Zod Schema
    const formSchema = z.object({
        email: z.string().email(),
        profile: z.enum(userProfilesList)
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: { email: '', profile: '' }
    });

    // Hooks
    const { stepTitle } = useStepConfig(
        step,
        () => ({
            0: {
                stepTitle: `${STEP_TITLE_PREFIX}.set_users`
            },
            1: {
                stepTitle: `${STEP_TITLE_PREFIX}.confirmation`
            }
        }),
        []
    );

    // Functions
    const handleAddMail = (index) => {
        const isEmailValid = renderList[index]?.isValid;
        const alreadyExist = renderList[index]?.alreadyExist;

        if (isEmailValid && !alreadyExist) {
            setRenderList([...renderList, { email: '', isValid: false }]);
        }
        setDisableAction(true);
    };

    const handleRemoveMail = (e, index) => {
        e.preventDefault();
        setRenderList(renderList.filter((_, idx) => idx !== index));
        setDisableAction(false);
        setMailEntryError('');
    };

    const getOrganizationProfileId = (profileName) => {
        if (userProfiles) {
            let profileId = '';
            for (const [key, value] of Object.entries(userProfiles)) {
                if (value.name === profileName) {
                    profileId = key;
                }
            }
            return profileId;
        }
    };

    const handleInputChange = (index, field, value) => {
        setRenderList((prevList) =>
            prevList.map((item, idx) => {
                if (idx === index) {
                    const newItem = { ...item, [field]: value };
                    if (field === 'email') {
                        newItem.isValid = z.string().email().safeParse(value).success;
                        newItem.alreadyExist = prevList.some(
                            (el, i) => el.email === value && i !== index
                        );
                        setMailEntryError(newItem.alreadyExist ? 'modal.send_mail.already_exists' : '');
                    }
                    else if (field === 'profile') {
                        newItem.profile = value;
                        newItem.profileId = getOrganizationProfileId(value);
                    }
                    setDisableAction(newItem.alreadyExist || !newItem.isValid || !newItem.email);
                    return newItem;
                }
                return item;
            })
        );
    };

    const onSuccess = () => {
        setIsOpen(false);
        storeRefetchUsers(true);
        toast({
            description: <Entity namespace={nsUser} entity='modal.send_mail.success' />,
            variant: 'success'
        });
    };
    const onError = (error) => {
        const errorCode = error.response?.data?.errorCode ?? error.code ?? 'UNKNOWN_ERROR';
        setIsOpen(false);
        toast({
            description: <Entity namespace={nsErrors} entity={`api.${errorCode}`} />,
            variant: 'danger'
        });
    };

    const submitCreateUser = actionsUsers.createUser(onSuccess, onError);

    const validateSendingMail = () => {
        const recipientsList = renderList.map(user => ({
            email: user.email,
            profileUuid: user.profileId,
            organization: organizationId || rootOrganization.uuid
        })
        );
        submitCreateUser.mutate(recipientsList);
    };

    const getUserProfilesOptions = () => {
        return buildDictionary(Object.values(userProfiles), 'name');
    };

    // Render
    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className={cn('max-w-4xl')}>
                <HeaderTitle title='modal.send_mail.title' namespace={nsUser} />
                <ModalBody className='body-send-mail-add-user'>
                    <Entity
                        entity={stepTitle}
                        data={{ step: step + 1, maxStep: MAX_STEPS_NUMBER }}
                        namespace={nsUser}
                        className={'mb-2 flex'}
                    />
                    <div className='send-mail-collection'>
                        <h5>
                            <Entity
                                className={cn('text-lg')}
                                entity='modal.send_mail.emails'
                                namespace={nsUser}
                            />
                        </h5>
                        <div className='flex justify-center'>
                            <Icon icon={ICONS.WARNING} className={cn('text-warning mr-1')} />
                            <Entity
                                className={cn('font-normal')}
                                entity='modal.send_mail.link_validity'
                                namespace={nsUser}
                            />
                        </div>
                    </div>
                    {step === 0 &&
                        <div>
                            <div className='addresses-list mt-4'>
                                <Form {...form}>
                                    <form>
                                        {renderList.map((mail, index) => (
                                            <div className='addresses-form-line' key={mail.email}>
                                                <FormField
                                                    name={`email-${index}`}
                                                    control={form.control}
                                                    render={({ field }) => (
                                                        <FormItem className='addresses-list-input'>
                                                            <FormControl>
                                                                <Input
                                                                    {...field}
                                                                    className={cn(mail.alreadyExist && 'border-red-500')}
                                                                    autoFocus
                                                                    type='email'
                                                                    value={mail.email}
                                                                    entity='modal.send_mail.placeholder'
                                                                    namespace={nsUser}
                                                                    autoComplete='off'
                                                                    onChange={(e) => {
                                                                        field.onChange(e);
                                                                        handleInputChange(index, 'email', e.target.value);
                                                                    }}
                                                                />
                                                            </FormControl>
                                                        </FormItem>
                                                    )}
                                                />
                                                <FormField
                                                    name={`profile-${index}`}
                                                    control={form.control}
                                                    render={({ field }) => (
                                                        <FormItem className='select-profile'>
                                                            <FormControl>
                                                                {userProfiles ?
                                                                    <Select
                                                                        {...field}
                                                                        options={getUserProfilesOptions()}
                                                                        namespace={userFields.PROFILE.namespace}
                                                                        prefixEntity={userFields.PROFILE.prefix}
                                                                        value={mail.profile}
                                                                        onChange={(value) => {
                                                                            handleInputChange(index, 'profile', value);
                                                                        }}
                                                                        disabled={disableAction}
                                                                    /> :
                                                                    <Spinner className='spinner create-user-spinner' />
                                                                }
                                                            </FormControl>
                                                        </FormItem>
                                                    )}
                                                />
                                                {renderList.length - 1 === index &&
                                                    renderList.length < MAX_EMAIL_TO_SEND ? (
                                                    <TooltipHelper
                                                        asChild
                                                        trigger={
                                                            <Button
                                                                onClick={() => handleAddMail(index)}
                                                                icon={ICONS.ADD}
                                                                variant='success-outline'
                                                                className={cn('ml-2 h-8 w-8 rounded-full border')}
                                                                disabled={disableAction || !mail.profile}
                                                            />
                                                        }
                                                        content={
                                                            <Entity
                                                                entity='modal.send_mail.add_address'
                                                                namespace={nsUser}
                                                            />
                                                        }
                                                    />
                                                ) : null}
                                                {index > 0 && (
                                                    <TooltipHelper
                                                        asChild
                                                        trigger={
                                                            <Button
                                                                onClick={(e) => handleRemoveMail(e, index)}
                                                                icon={ICONS.REMOVE}
                                                                variant='danger-outline'
                                                                className={cn('ml-2 h-8 w-8 rounded-full border')}
                                                            />
                                                        }
                                                        content={
                                                            <Entity
                                                                entity='modal.send_mail.remove_address'
                                                                namespace={nsUser}
                                                            />
                                                        }
                                                    />
                                                )}
                                            </div>
                                        ))}
                                    </form>
                                </Form>
                            </div>
                            <Entity
                                className='max-mail-number'
                                namespace={nsUser}
                                entity='modal.send_mail.maximum_mails'
                            />
                            <Entity
                                className='mail-entry-error'
                                namespace={nsUser}
                                entity={mailEntryError}
                            />
                        </div >
                    }
                    {step === 1 &&
                        <div className='confirmation'>
                            <div>
                                <div className='confirmation-list'>
                                    {
                                        renderList.length === 1 ?
                                            <Entity namespace={nsUser} entity='modal.send_mail.confirmation' />
                                            :
                                            <Entity namespace={nsUser} entity='modal.send_mail.confirmation_users' />
                                    }
                                </div>
                                <div>
                                    {

                                        renderList.map((element) =>
                                            <ul key={element.email}>
                                                <li>{element.email}</li>
                                            </ul>
                                        )
                                    }
                                </div>
                                <div className='confirmation-validation-text'>
                                    <Entity namespace={nsUser} entity='modal.send_mail.validate' />
                                </div>
                            </div>
                        </div>
                    }
                </ModalBody>
                <FooterStep
                    step={step}
                    setStep={setStep}
                    stepsNumber={MAX_STEPS_NUMBER}
                    onValidate={validateSendingMail}
                    isLoading={submitCreateUser.isLoading}
                    disableValidation={disableAction || !renderList.every((elem) => elem.profile)}
                />
            </ModalContent>
        </Modal>
    );
}

ModalSendMailAddUser.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    organizationId: PropTypes.string
};

export default ModalSendMailAddUser;